export interface SystemSettingsRaw {
  key: string;
  value: unknown;
}

export interface SystemSettings {
  late_tolerance_minutes: number;
  allowed_checkin_radius: number;
  payment_grace_days: number;
  payment_grace_reminder_hours: number;
  payment_grace_timezone: string;
  app_min_version_android: string | null;
  app_min_version_ios: string | null;
  app_recommended_version_android: string | null;
  app_recommended_version_ios: string | null;
  app_store_url_android: string | null;
  app_store_url_ios: string | null;
}

/**
 * Keys readable by any authenticated user.
 * MUST stay in sync with the RLS policy `system_settings_select_public_keys`.
 * Any new key is admin-only by default until added here AND to the policy.
 */
export const PUBLIC_SETTING_KEYS = [
  'late_tolerance_minutes',
  'allowed_checkin_radius',
  'payment_grace_days',
  'payment_grace_reminder_hours',
  'payment_grace_timezone',
  'audio_recording_max_duration',
  'app_min_version_android',
  'app_min_version_ios',
  'app_recommended_version_android',
  'app_recommended_version_ios',
  'app_store_url_android',
  'app_store_url_ios',
] as const;

export const DEFAULT_SETTINGS: SystemSettings = {
  late_tolerance_minutes: 15,
  allowed_checkin_radius: 500,
  payment_grace_days: 3,
  payment_grace_reminder_hours: 24,
  payment_grace_timezone: 'America/Sao_Paulo',
  app_min_version_android: null,
  app_min_version_ios: null,
  app_recommended_version_android: null,
  app_recommended_version_ios: null,
  app_store_url_android: null,
  app_store_url_ios: null,
};


const toNumber = (v: unknown, fallback: number): number => {
  if (typeof v === 'number') return v;
  return Number(v) || fallback;
};

const toString = (v: unknown, fallback: string): string => {
  if (typeof v === 'string' && v.trim() !== '') return v;
  if (v != null) {
    try {
      const s = JSON.parse(JSON.stringify(v));
      if (typeof s === 'string' && s.trim() !== '') return s;
    } catch { /* noop */ }
  }
  return fallback;
};

/**
 * Parses raw system settings rows into typed SystemSettings.
 * Falls back to defaults for missing or invalid values.
 */
export function parseSystemSettings(rows: SystemSettingsRaw[]): SystemSettings {
  const settings = { ...DEFAULT_SETTINGS };

  rows.forEach((setting) => {
    switch (setting.key) {
      case 'late_tolerance_minutes':
        settings.late_tolerance_minutes = toNumber(setting.value, DEFAULT_SETTINGS.late_tolerance_minutes);
        break;
      case 'allowed_checkin_radius':
        settings.allowed_checkin_radius = toNumber(setting.value, DEFAULT_SETTINGS.allowed_checkin_radius);
        break;
      case 'payment_grace_days':
        settings.payment_grace_days = toNumber(setting.value, DEFAULT_SETTINGS.payment_grace_days);
        break;
      case 'payment_grace_reminder_hours':
        settings.payment_grace_reminder_hours = toNumber(setting.value, DEFAULT_SETTINGS.payment_grace_reminder_hours);
        break;
      case 'payment_grace_timezone':
        settings.payment_grace_timezone = toString(setting.value, DEFAULT_SETTINGS.payment_grace_timezone);
        break;
      case 'app_min_version_android':
        settings.app_min_version_android = setting.value != null ? toString(setting.value, '') || null : null;
        break;
      case 'app_min_version_ios':
        settings.app_min_version_ios = setting.value != null ? toString(setting.value, '') || null : null;
        break;
      case 'app_recommended_version_android':
        settings.app_recommended_version_android = setting.value != null ? toString(setting.value, '') || null : null;
        break;
      case 'app_recommended_version_ios':
        settings.app_recommended_version_ios = setting.value != null ? toString(setting.value, '') || null : null;
        break;
      case 'app_store_url_android':
        settings.app_store_url_android = setting.value != null ? toString(setting.value, '') || null : null;
        break;
      case 'app_store_url_ios':
        settings.app_store_url_ios = setting.value != null ? toString(setting.value, '') || null : null;
        break;
    }
  });

  return settings;
}
